"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

type Props = {
	totalPages: number;
};

function getMaxTargetSize() {
	if (typeof window === "undefined" || window.innerWidth >= 640) return 8;
	if (window.innerWidth >= 400) return 4;

	return 3;
}

/**
 * Note(Curstantine):
 * Finds the "values" of the middle dynamic row.
 * Example (current: 9, total: 12):
 * 1. Start -> max(2, 9 - (8 - 2)) => 3
 * 2. End -> min(11, 3 + 8 - 2) => 11
 *
 * Start gets recalculated whenever {@link end} is {@link currentPage} - 1
 * max(2, 12 - 8 + 1) => 5
 */
function createTargets(totalPages: number, currentPage: number) {
	const maxVisiblePages = getMaxTargetSize();
	const pages = [1];

	let start = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
	let end = Math.min(totalPages - 1, start + maxVisiblePages - 2);

	if (end === totalPages - 1) start = Math.max(2, totalPages - maxVisiblePages + 1);

	if (start > 2) pages.push(-1);

	for (let i = start; i <= end; i++) {
		pages.push(i);
	}

	if (end < totalPages - 1) pages.push(-1);
	if (totalPages > 1) pages.push(totalPages);

	return pages;
}

const chevronClass = cn(
	"size-6 shrink-0 text-[color:var(--color-brand-primary)] transition-opacity hover:opacity-80",
	"[&>svg]:stroke-[color:var(--color-brand-primary)] [&>svg]:stroke-[1.75]",
);

const pageBaseClass =
	"inline-flex size-9 items-center justify-center rounded-full text-sm font-medium transition-colors xs:size-10 xs:text-base";

/**
 * Inactive pages: same hairline as filter sheet (`border-[0.5px]`), Techxeed blue at low alpha.
 */
const pageInactiveClass = cn(
	"border-solid border-[0.5px] border-[color:color-mix(in_srgb,var(--color-brand-primary)_42%,transparent)] bg-transparent",
	"text-muted-foreground hover:text-foreground/90 hover:border-[color:color-mix(in_srgb,var(--color-brand-primary)_65%,transparent)] hover:bg-[color:color-mix(in_srgb,var(--color-brand-primary)_08%,transparent)]",
	"dark:hover:bg-white/5",
);

/** Hairline placeholder ring so active circle matches inactive outer size (filter sheet line weight). */
const pageActiveClass =
	"border-solid border-[0.5px] border-transparent bg-[color:var(--color-brand-primary)] text-white shadow-sm";

export default function PaginationBar({ totalPages }: Props) {
	const pathname = usePathname();
	const params = useSearchParams();

	const currentPage = Number.parseInt(params.get("page") ?? "1", 10);
	const items = createTargets(totalPages, currentPage);

	const createUrl = (page: number) => {
		const temp = new URLSearchParams(params);
		temp.set("page", page.toString());
		return `${pathname}?${temp}`;
	};
	if (totalPages === 1) return null;

	return (
		<div
			className="mx-auto flex min-h-14 max-w-full items-center justify-center gap-2 px-6 py-4 sm:gap-3 sm:px-12 sm:py-5 md:px-16"
			role="navigation"
			aria-label="Pagination"
		>
			{currentPage > 1 ? (
				<Link href={createUrl(currentPage - 1)} className={chevronClass} aria-label="Previous page">
					<ChevronLeft className="size-6" aria-hidden />
				</Link>
			) : (
				<span className={cn(chevronClass, "pointer-events-none opacity-35")} aria-hidden>
					<ChevronLeft className="size-6" />
				</span>
			)}

			{items.map((i, idx) => {
				if (i === -1) {
					return (
						<span
							key={`ellipsis-${idx}`}
							className="px-1 text-sm text-muted-foreground dark:text-white/60"
							aria-hidden
						>
							…
						</span>
					);
				}

				const isActive = currentPage === i;

				if (isActive) {
					return (
						<span
							key={i}
							className={cn(pageBaseClass, pageActiveClass)}
							aria-current="page"
						>
							{i}
						</span>
					);
				}

				return (
					<Link
						key={i}
						href={createUrl(i)}
						className={cn(pageBaseClass, pageInactiveClass)}
					>
						{i}
					</Link>
				);
			})}

			{currentPage !== totalPages ? (
				<Link href={createUrl(currentPage + 1)} className={chevronClass} aria-label="Next page">
					<ChevronRight className="size-6" aria-hidden />
				</Link>
			) : (
				<span className={cn(chevronClass, "pointer-events-none opacity-35")} aria-hidden>
					<ChevronRight className="size-6" />
				</span>
			)}
		</div>
	);
}
